/* eslint-disable react/prop-types */
import { useEffect, useState, useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "../../../api/axios";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DataTable from "examples/Tables/DataTable";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

export default function PayrollPreview() {
  const location = useLocation();
  const navigate = useNavigate();

  const [month, setMonth] = useState(() => {
    const d = new Date();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    return `${d.getFullYear()}-${m}`;
  });

  const [search, setSearch] = useState("");
  const [rows, setRows] = useState([]);
  const [perPage, setPerPage] = useState(50);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const normalizeRows = (payload) => {
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload)) return payload;
    return [];
  };

  const mapRows = (items) => {
    return (items || []).map((item, index) => ({
      id: item?.id ?? `preview-${index}-${item?.pf_no ?? item?.pfno ?? ""}`,
      pf_no: item?.pf_no ?? item?.pfno ?? "",
      gross: item?.gross ?? 0,
      deductions: item?.deductions ?? item?.total_deductions ?? 0,
      net: item?.net ?? 0,
      created_at: item?.created_at ?? "",
    }));
  };

  const logAxios = (e) => {
    // eslint-disable-next-line no-console
    console.log("AXIOS DEBUG:", {
      baseURL: e?.config?.baseURL,
      url: e?.config?.url,
      fullURL: `${e?.config?.baseURL || ""}${e?.config?.url || ""}`,
      method: e?.config?.method,
      status: e?.response?.status,
      response: e?.response?.data,
    });
  };

  useEffect(() => {
    const qs = new URLSearchParams(location.search);
    const qMonth = qs.get("month");
    const qSearch = qs.get("search");

    if (qMonth && /^\d{4}-\d{2}$/.test(qMonth)) {
      setMonth(qMonth);
    }

    if (qSearch != null) {
      setSearch(qSearch);
    }
  }, [location.search]);

  useEffect(() => {
    const qs = new URLSearchParams(location.search);

    if (month) {
      qs.set("month", month);
    }

    if (search.trim()) {
      qs.set("search", search.trim());
    } else {
      qs.delete("search");
    }

    navigate(
      {
        pathname: location.pathname,
        search: qs.toString(),
      },
      { replace: true }
    );
  }, [month, search, location.pathname, location.search, navigate]);

  const loadPreview = useCallback(async () => {
    setError("");
    setLoading(true);

    try {
      const res = await axios.get("/payroll-run/preview", {
        params: {
          month,
          search: search.trim(),
          per_page: perPage,
        },
      });

      const payload = res?.data ?? {};
      const paginated = payload?.data ?? {};
      const normalized = normalizeRows(paginated);
      setRows(mapRows(normalized));
    } catch (e) {
      logAxios(e);
      setRows([]);
      setError(e?.response?.data?.message || e?.message || "Failed to load payroll preview.");
    } finally {
      setLoading(false);
    }
  }, [month, search, perPage]);

  useEffect(() => {
    loadPreview();
  }, [loadPreview]);

  const columns = useMemo(
    () => [
      {
        Header: "PFNO",
        accessor: "pf_no",
        Cell: ({ value }) => value || "-",
      },
      {
        Header: "Gross",
        accessor: "gross",
        Cell: ({ value }) => (value == null ? "-" : Number(value).toLocaleString()),
      },
      {
        Header: "Deductions",
        accessor: "deductions",
        Cell: ({ value }) => (value == null ? "-" : Number(value).toLocaleString()),
      },
      {
        Header: "Net",
        accessor: "net",
        Cell: ({ value }) => (value == null ? "-" : Number(value).toLocaleString()),
      },
      {
        Header: "Created At",
        accessor: "created_at",
        Cell: ({ value }) => value || "-",
      },
    ],
    []
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <MDBox py={3}>
        <MDTypography variant="h5">Payroll Preview</MDTypography>
        <MDTypography variant="button" color="text">
          Preview results from generated payroll items for the selected month.
        </MDTypography>

        {!!error && (
          <MDBox mt={2} p={2} borderRadius={2} sx={{ border: "1px solid #f5c2c7" }}>
            <MDTypography variant="button" color="error">
              {error}
            </MDTypography>
          </MDBox>
        )}

        <MDBox mt={3} display="flex" gap={2} alignItems="center" flexWrap="wrap">
          <input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            style={{ padding: 8, borderRadius: 6, border: "1px solid #ccc" }}
          />

          <MDButton color="info" onClick={loadPreview} disabled={loading}>
            {loading ? "Loading..." : "Refresh Preview"}
          </MDButton>
        </MDBox>

        <MDBox
          mt={3}
          display="flex"
          justifyContent="flex-end"
          alignItems="center"
          flexWrap="wrap"
          gap={2}
        >
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search PFNO, gross, deductions, net..."
            style={{
              padding: 10,
              borderRadius: 6,
              border: "1px solid #ccc",
              minWidth: 280,
            }}
          />
        </MDBox>

        <MDBox mt={3}>
          <DataTable
            table={{ columns, rows }}
            entriesPerPage
            showTotalEntries
            noEndBorder
            canSearch={false}
          />
        </MDBox>
      </MDBox>

      <Footer />
    </DashboardLayout>
  );
}
